import type { ReactElement } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useTranslate } from '../lib/i18n-provider'
import { useSession } from '../lib/session-context'

/**
 * Poortwachter voor de schermen die een sessie vereisen.
 *
 * Wordt als layout-route gebruikt: de beschermde routes staan eronder en komen
 * via `<Outlet />` naar buiten. Zolang de sessie nog wordt opgehaald is er niets
 * te beslissen — dan verschijnt de laadtoestand. Meteen naar /login sturen zou
 * betekenen dat iedereen bij het eerste beeld even uitgelogd lijkt.
 *
 * Let op wat dit *niet* is: dit is geen beveiliging. De echte controle staat in
 * de API en in de RLS-policies (§17.3). Dit voorkomt alleen dat iemand naar een
 * scherm kijkt dat toch geen data zou tonen.
 */

/**
 * Wat er in `location.state` meegaat bij het doorsturen: het pad waar de bezoeker
 * heen wilde. Het inlogscherm brengt hem daar na afloop terug.
 */
export interface RedirectState {
  readonly from?: string
}

/**
 * Leest het terugkeerpad uit een router-state en valt terug op de startpagina.
 *
 * De controle is streng met opzet. `location.state` overleeft een
 * `history.pushState` en is dus door de bezoeker te beïnvloeden; alleen een
 * relatief pad wordt geaccepteerd. Een waarde die met `//` begint is voor de
 * browser een protocol-relatieve URL naar een andere host — precies de open
 * redirect die je op een inlogscherm niet wilt.
 */
export function readRedirectTarget(state: unknown): string {
  if (typeof state !== 'object' || state === null) {
    return '/'
  }
  const from: unknown = (state as { from?: unknown }).from
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) {
    return '/'
  }
  return from
}

export function ProtectedRoute(): ReactElement {
  const t = useTranslate()
  const { state } = useSession()
  const location = useLocation()

  if (state.phase === 'loading') {
    return (
      <p className="page__status" role="status">
        {t('common.state.loading')}
      </p>
    )
  }

  if (state.phase === 'anonymous') {
    // `replace`: het beschermde pad hoort niet in de geschiedenis te blijven
    // staan, anders komt de bezoeker er met de terugknop meteen weer op uit.
    const from = `${location.pathname}${location.search}`
    return <Navigate to="/login" replace state={{ from } satisfies RedirectState} />
  }

  return <Outlet />
}
